import { differenceInMinutes, format } from "date-fns";
import escape from "lodash/escape";

import { APPOINTMENT_STATUSES } from "constants/appointments";
import type { Appointment } from "services/types/appointments.type";
import { getFullName } from "utils/common";

import { getPatientSignatureMergeTagOrPlaceholder } from "../merge-tags.utils";
import type { AppointmentContext } from "./form-context-values.utils";

const TIME_FORMAT = "h:mm aa";

// These values go into the form as HTML, so what someone typed must stay text: it can't add markup or run a script.
const asText = (value?: string | null) => escape(value ?? "");

const describeDuration = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return [hours && `${hours} hr`, rest && `${rest} min`]
    .filter(Boolean)
    .join(" ");
};

// As the Anvil form: exactly one pair fills, and the other prints nothing (the same test as `supervisorIn`).
// A supervisor with no signature prints blank, where the shared tag would show the sign button.
const supervisorPair = (appointment: Appointment, direct: boolean) => {
  const supervisor = appointment.supervising_provider;
  if (!supervisor || Boolean(appointment.is_direct_supervisor) !== direct) {
    return { name: "", signature: "" };
  }
  return {
    name: asText(
      getFullName({
        firstName: supervisor.first_name || "",
        lastName: supervisor.last_name || "",
        title: supervisor.title,
        suffix: supervisor.suffix || ""
      })
    ),
    signature: supervisor.signature
      ? getPatientSignatureMergeTagOrPlaceholder(supervisor.signature)
      : ""
  };
};

/**
 * The Appointment tags' values, from the appointment a form is printed for and nothing else, so a form in a bulk
 * run can't print another appointment's. `startDate` and `endDate` already hold the clinic's wall time
 * (convertAppointmentResponseToModel), so they format the same wherever the person printing is.
 */
export const appointmentFormValues = (
  appointment: Appointment
): AppointmentContext => {
  const start = appointment.startDate ? new Date(appointment.startDate) : null;
  const end = appointment.endDate ? new Date(appointment.endDate) : null;
  const staffMember = appointment.staffMember;
  const referralSource = appointment.referralSource;
  const direct = supervisorPair(appointment, true);
  const supervising = supervisorPair(appointment, false);

  return {
    type: asText(appointment.appointmentType?.name),
    status: APPOINTMENT_STATUSES.getFromCode(appointment.status) ?? "",
    date: start ? format(start, "MM/dd/yyyy") : "",
    time: start ? format(start, TIME_FORMAT) : "",
    timeRange:
      start && end
        ? `${format(start, TIME_FORMAT)} – ${format(end, TIME_FORMAT)}`
        : "",
    duration:
      start && end ? describeDuration(differenceInMinutes(end, start)) : "",
    room: asText(appointment.room?.name),
    clinic: asText(appointment.clinic?.name),
    notes: asText(appointment.notes),
    companion: asText(appointment.companionName),
    telehealthLink: asText(appointment.zoom_join_url),
    referralSource:
      referralSource && typeof referralSource === "object"
        ? asText(referralSource.name)
        : "",
    // Never `license_number`: that fallback is how forms printed another state's license (accounts/CLAUDE.md).
    providerLicenseNumber: asText(staffMember?.resolved_license_number),
    providerNpi: asText(staffMember?.npi),
    directSupervisorName: direct.name,
    directSupervisorSignature: direct.signature,
    supervisorName: supervising.name,
    supervisorSignature: supervising.signature
  };
};
